<template>
  <div class="analytics-filters mb-2">
    <!-- Single row: Timespan + dimension filters + reset -->
    <div class="filters-row">
      <analytics-timespan-filter
        ref="timespanFilter"
        @change="onTimespanChange"
      />

      <multi-select-filter
        label="Retailer"
        :options="retailers.map(r => ({ value: r.id, text: r.name }))"
        :value="localFilters.retailerId"
        @input="v => setFilter('retailerId', v)"
      />
      <multi-select-filter
        label="Collection"
        :options="collections.map(c => ({ value: c.id, text: c.name }))"
        :value="localFilters.collectionId"
        @input="v => setFilter('collectionId', v)"
      />
      <multi-select-filter
        label="Product"
        :options="products.map(p => ({ value: p.id, text: p.name || p.id }))"
        :value="localFilters.productId"
        @input="v => setFilter('productId', v)"
      />
      <multi-select-filter
        label="Department"
        :options="departments.map(d => ({ value: d, text: d }))"
        :value="localFilters.department"
        @input="v => setFilter('department', v)"
      />
      <multi-select-filter
        label="Category"
        :options="categories.map(c => ({ value: c, text: c }))"
        :value="localFilters.category"
        @input="v => setFilter('category', v)"
      />
      <multi-select-filter
        label="Subcategory"
        :options="subcategories.map(s => ({ value: s, text: s }))"
        :value="localFilters.subcategory"
        @input="v => setFilter('subcategory', v)"
      />
      <multi-select-filter
        label="Gender"
        :options="genders.map(g => ({ value: g, text: g }))"
        :value="localFilters.gender"
        @input="v => setFilter('gender', v)"
      />
      <multi-select-filter
        label="Color"
        :options="colors.map(c => ({ value: c, text: c }))"
        :value="localFilters.color"
        @input="v => setFilter('color', v)"
      />

      <div class="filters-right">
        <span
          v-if="activeFilterCount > 0"
          class="active-badge"
        >
          {{ activeFilterCount }} filter{{ activeFilterCount > 1 ? 's' : '' }} active
        </span>
        <button
          v-b-tooltip.hover
          title="Reset all filters"
          class="reset-btn"
          type="button"
          @click="resetFilters"
        >
          <feather-icon
            icon="RotateCcwIcon"
            size="13"
          />
          <span>Reset</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { VBTooltip } from 'bootstrap-vue'
import MultiSelectFilter from './MultiSelectFilter.vue'
import AnalyticsTimespanFilter from './AnalyticsTimespanFilter.vue'

const emptyArrayFilters = () => ({
  retailerId: [],
  collectionId: [],
  productId: [],
  department: [],
  category: [],
  subcategory: [],
  gender: [],
  color: [],
})

export default {
  name: 'AnalyticsFilters',
  components: { MultiSelectFilter, AnalyticsTimespanFilter },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    retailers: { type: Array, default: () => [] },
    collections: { type: Array, default: () => [] },
    products: { type: Array, default: () => [] },
    genders: { type: Array, default: () => [] },
    departments: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] },
    subcategories: { type: Array, default: () => [] },
    colors: { type: Array, default: () => [] },
  },
  data() {
    return {
      localFilters: {
        startDate: null,
        endDate: null,
        ...emptyArrayFilters(),
      },
    }
  },
  computed: {
    activeFilterCount() {
      const keys = ['retailerId', 'collectionId', 'productId', 'department', 'category', 'subcategory', 'gender', 'color']
      return keys.filter(k => this.localFilters[k]?.length > 0).length
    },
  },
  methods: {
    onTimespanChange({ startDate, endDate }) {
      this.localFilters.startDate = startDate
      this.localFilters.endDate = endDate
      this.onFilterChange()
    },
    setFilter(key, values) {
      this.localFilters[key] = values
      this.onFilterChange()
    },
    onFilterChange() {
      this.$emit('filter-change', this.cleanFilters())
    },
    cleanFilters() {
      const out = {}
      if (this.localFilters.startDate) out.startDate = this.localFilters.startDate
      if (this.localFilters.endDate) out.endDate = this.localFilters.endDate
      const arrayKeys = ['retailerId', 'collectionId', 'productId', 'department', 'category', 'subcategory', 'gender', 'color']
      arrayKeys.forEach(k => {
        if (this.localFilters[k]?.length) {
          out[k] = this.localFilters[k].join(',')
        }
      })
      return out
    },
    resetFilters() {
      this.localFilters = {
        startDate: null,
        endDate: null,
        ...emptyArrayFilters(),
      }
      this.$refs.timespanFilter.reset()
    },
  },
}
</script>

<style lang="scss" scoped>
@mixin focus-ring {
  &:focus-visible {
    outline: 2px solid #1e40af;
    outline-offset: 2px;
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
}

.analytics-filters {
  background: #fff;
  border: 1px solid #e9eef6;
  border-radius: 10px;
  padding: 12px 16px;
}

.filters-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.filters-right {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.active-badge {
  font-size: 0.7rem;
  font-weight: 600;
  color: #1e40af;
  background: #dbeafe;
  padding: 4px 10px;
  border-radius: 10px;
  white-space: nowrap;
  min-height: 24px;
  display: inline-flex;
  align-items: center;
}

.reset-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid #e9eef6;
  background: #f8fafc;
  color: #64748b;
  font-size: 0.775rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
  @include focus-ring;

  &:hover {
    border-color: #94a3b8;
    color: #1e3a8a;
    background: #eff6ff;
  }
}

@media (prefers-reduced-motion: reduce) {
  .reset-btn { transition: none; }
}
</style>
